/**
 * ReservationPipeline — the CRM-style board: leads grouped into pipeline-stage
 * columns. Each card opens the detail Sheet. This is a read surface; all writes
 * happen in the detail panel.
 */

import { useFormatters, useT } from '@hostesa/i18n';
import { StatusBadge } from '@hostesa/ui';
import type { Reservation, ReservationStatus } from './_deps';
import { asNumber, asString } from './_format';
import { namespace } from './i18n';
import { PIPELINE_STAGES, reservationStatusVariant } from './transitions';

interface ReservationCardProps {
  reservation: Reservation;
  onOpen: (reservation: Reservation) => void;
}

function ReservationCard({ reservation, onOpen }: ReservationCardProps) {
  const { t } = useT(namespace);
  const { formatDate } = useFormatters('UTC');

  const adults = asNumber(reservation.adults) ?? 0;
  const children = asNumber(reservation.children) ?? 0;
  const arrival = asString(reservation.arrivalDate);

  return (
    <button
      type="button"
      data-testid={`reservation-card-${reservation.id}`}
      onClick={() => onOpen(reservation)}
      className="flex w-full flex-col gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-alabaster)] p-3.5 text-left shadow-[var(--shadow-card)] transition-colors hover:border-[var(--color-warm-stone)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
      aria-label={t('card.openDetail')}
    >
      <span className="text-sm font-medium text-[var(--color-charcoal)]">
        {asString(reservation.contactLanguage)
          ? `${t('card.unnamedGuest')} · ${asString(reservation.contactLanguage)}`
          : t('card.unnamedGuest')}
      </span>
      {arrival && (
        <span className="font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
          {formatDate(arrival)}
        </span>
      )}
      <span className="font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
        {t('card.party', { adults, children })}
      </span>
    </button>
  );
}

export interface ReservationPipelineProps {
  reservations: readonly Reservation[];
  onOpen: (reservation: Reservation) => void;
}

export function ReservationPipeline({ reservations, onOpen }: ReservationPipelineProps) {
  const { t } = useT(namespace);

  const byStage = new Map<ReservationStatus, Reservation[]>();
  for (const stage of PIPELINE_STAGES) byStage.set(stage, []);
  for (const r of reservations) {
    const bucket = byStage.get(r.status);
    if (bucket) bucket.push(r);
  }

  return (
    <div
      data-testid="reservation-pipeline"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      {PIPELINE_STAGES.map((stage) => {
        const items = byStage.get(stage) ?? [];
        return (
          <section
            key={stage}
            data-testid={`reservation-stage-${stage}`}
            className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-canvas)] p-3"
          >
            <header className="flex items-center justify-between">
              <StatusBadge
                data-testid={`reservation-stage-badge-${stage}`}
                status={reservationStatusVariant(stage)}
                label={t(`status.${stage}`)}
              />
              <span className="font-[var(--font-mono)] text-xs text-[var(--color-stone-mid)]">
                <span className="sr-only">{t('pipeline.countLabel', { count: items.length })}</span>
                <span aria-hidden="true">{items.length}</span>
              </span>
            </header>
            <div className="flex flex-col gap-2">
              {items.map((reservation) => (
                <ReservationCard key={reservation.id} reservation={reservation} onOpen={onOpen} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
